import {ChangeEvent, memo, ReactNode, useCallback, useEffect, useState} from "react";
import {Option, Select} from "@material-tailwind/react";
import {FooterListItem} from "./Sidebar/FooterMenu";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import {useTranslation} from "react-i18next";

const getItemValue = (item: FooterListItem): string | number => {
    const value = item.value ?? item.id ?? item.code ?? item.name;
    if (typeof value === 'number' || typeof value === 'string') {
        return value;
    }
    return '';
};

const areValuesEqual = (a?: string | number, b?: string | number) => {
    if (a === undefined || a === null || b === undefined || b === null) {
        return false;
    }
    return a.toString() === b.toString();
};

export interface DropdownInputProps {
    name: string,
    list: FooterListItem[],
    onChange: (s: string | number) => void,
    onSelectAll?: () => void,
    selected?: number | string | Array<number | string>,
    className?: string,
    id?: string,
    buttonClassName?: string,
    menuClassName?: string,
    labelClassName?: string,
    searchable?: boolean,
    multiple?: boolean,
    withLabel?: boolean,
    withAllButton?: boolean,
    onRemove?: (n: number) => void,
    onClear?: () => void,
    withLimit?: boolean,
    selectedItems?: FooterListItem[],
    renderOption?: (item: FooterListItem, isSelected: boolean) => ReactNode,
    noResultsActionLabel?: (query: string) => string,
    noResultsActionValidator?: (query: string) => boolean,
    onNoResultsAction?: (query: string) => void
}

const DropdownInput = ({
                           name,
                           list,
                           selected,
                           onChange,
                           buttonClassName,
                           menuClassName,
                           id,
                           searchable = false,
                           multiple = false,
                           withAllButton = false,
                           withLabel = true,
                           withLimit = false,
                           onRemove,
                           onSelectAll,
                           onClear,
                           selectedItems,
                           renderOption,
                           noResultsActionLabel,
                           noResultsActionValidator,
                           onNoResultsAction
                       }: DropdownInputProps) => {

    const {t} = useTranslation();
    const [localList, setLocalList] = useState<FooterListItem[]>([]);
    const [search, setSearch] = useState<string>('');
    const setSearchInputRef = useCallback((node: HTMLInputElement | null) => {
        if (node && searchable) {
            setTimeout(function(){
                node.focus({preventScroll: true});
            }, 300);
        }
    }, [searchable]);

    const singleLabel = useCallback(() => {
        const singleSelected = typeof selected === 'string' || typeof selected === 'number' ? selected : undefined;
        const item = list.find(i => areValuesEqual(getItemValue(i), singleSelected));
        if (item) {
            return item.name;
        }
        return name;
    }, [list, selected, name]);

    const onLocalRemove = useCallback((index: number) => {
        if (onRemove) {
            onRemove(index);
        }
    }, [onRemove]);

    const multipleLabel = useCallback(() => {
        if (selected && Array.isArray(selected) && !withLabel)
            return <>{name} ({selected.length})</>;
        const selectedArray = Array.isArray(selected) ? selected : [];
        const items = (selectedItems && selectedItems.length > 0 ? selectedItems : selectedArray.map((value) => {
            return list.find(i => areValuesEqual(getItemValue(i), value));
        }).filter((item): item is FooterListItem => !!item)) ?? [];
        const prefix = selectedArray.length > 5 ?
            <div className="w-full">{name} ({selectedArray.length})</div> : <></>;
        return selectedArray.length > 0 ? <>
            {prefix}
            {items.map((item, index) => {
                return <span key={`dropdown-input-${name}-${index}`}
                             className="bg-blue-gray-50 dark:bg-black dark:text-white px-1.5 py-0.5 inline-block min-w-[4rem] rounded-md">
                <span className="flex items-center w-fit">
                {item.icon ?? ''}{item.name}
                    <i className="ml-1 text-red-400" onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        onLocalRemove(index);
                    }}><FontAwesomeIcon
                        icon={solid('xmark')}
                        className="w-5"/></i>
                    </span>
            </span>
            })}
        </> : name;
    }, [list, selected, name, withLabel, onLocalRemove, selectedItems]);

    const label = useCallback(() => {
        if (!multiple)
            return singleLabel();
        return multipleLabel();
    }, [multiple, singleLabel, multipleLabel]);

    const onSearchChanged = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.currentTarget) {
            const query = e.currentTarget.value.toLowerCase();
            setSearch(e.currentTarget.value);
            const filtered = list.filter(i => {
                const matchName = i.name.toString().toLowerCase().includes(query);
                const matchCode = i.code ? i.code.toString().toLowerCase().includes(query) : false;
                return matchName || matchCode;
            });
            if (withLimit)
                setLocalList(filtered.slice(0, 10));
            else
                setLocalList(filtered);
        }
    }

    const setList = useCallback(() => {
        if (withLimit)
            setLocalList(searchable && list.length > 10 ? list.slice(0, 10) : list);
        else
            setLocalList(list);
    }, [searchable, withLimit, list])

    const onItemClicked = useCallback((index: number) => {
        const item = localList[index];
        setSearch('');
        setList();
        if (!item)
            return;
        const value = getItemValue(item);
        if (multiple && Array.isArray(selected)) {
            const selectedIndex = selected.findIndex((entry) => areValuesEqual(entry, value));
            if (selectedIndex !== -1) {
                onLocalRemove(selectedIndex);
            } else {
                onChange(value);
            }
        } else {
            onChange(value);
        }
    }, [localList, selected, multiple, onChange, onLocalRemove, setList]);

    const onSelectAllClicked = useCallback(() => {
        setSearch('');
        if (onSelectAll)
            onSelectAll();
    }, [onSelectAll]);

    useEffect(() => {
        setList()
    }, [setList]);

    const trimmedSearch = search.trim();
    const showNoResultsAction = Boolean(
        searchable &&
        noResultsActionLabel &&
        onNoResultsAction &&
        trimmedSearch.length > 0 &&
        localList.length === 0 &&
        (!noResultsActionValidator || noResultsActionValidator(trimmedSearch))
    );

    const onCustomActionClick = useCallback(() => {
        if (!onNoResultsAction || !trimmedSearch) {
            return;
        }
        onNoResultsAction(trimmedSearch);
        setSearch('');
        setList();
    }, [onNoResultsAction, trimmedSearch, setList]);


    return <div className="mb-4 lg:mb-auto" id={id ? '#' + id : ''}>
        <Select
            id={id ? '#' + id : ''}
            value={label() as string}
            arrow={<span className="empty-data"/>}
            className={`dropdown-button-select normal-case ${multiple ? 'min-h-[2.5rem] multiple-select' : 'flex'} items-center text-left w-full peer h-full bg-transparent text-blue-gray-700 font-sans font-normal outline outline-0 focus:outline-0 disabled:bg-blue-gray-50 disabled:border-0 disabled:cursor-not-allowed transition-all placeholder-shown:border placeholder-shown:border-blue-gray-200 placeholder-shown:border-t-blue-gray-200 border focus:border-2 placeholder:opacity-0 focus:placeholder:opacity-100 text-sm px-3 py-2.5 rounded-[7px] border-blue-gray-200 focus:border-gray-900 dark:text-gray-500 dark:border-blue-gray-800 dark:placeholder-shown:border-blue-gray-800 dark:focus:border-blue-gray-800 shadow-none hover:shadow-none ${buttonClassName ?? ''}`}
            labelProps={{
                className: "hidden"
            }}
            menuProps={{
                className: `select-dropdown-classes max-w-md dark:bg-blue-gray-900 dark:text-gray-500 dark:border-blue-gray-800 ${menuClassName ?? 'w-full'}`,
                placement: 'bottom'
            }}
        >
            {searchable ? <input id={`search-input-footer-menu-${id}`}
                                 autoComplete="off"
                                 className="w-full px-3 py-1 rounded border border-gray-700 mb-2 bg-transparent outline-none placeholder:opacity-100 placeholder:text-gray-700"
                                 placeholder={t('admin.datatable.search')}
                                 value={search}
                                 ref={setSearchInputRef}
                                 onKeyDown={(e) => { if (e.key.length === 1) e.stopPropagation(); }}
                                 onChange={onSearchChanged}/> : ''}
            {searchable && withLimit && list.length > 10 ?
                <div className="text-xs px-2 mb-2 text-gray-700 outline-none">{t('admin.datatable.only_10')}</div> : ''}
            {withAllButton && onClear && (multiple && Array.isArray(selected) && selected.length > 0) ?
                <div className="flex justify-between items-center w-full">
                    <Option value="0" className="w-full uppercase font-bold text-[10px] text-left bg-transparent-hover"
                            onClick={() => onSelectAllClicked()}>{t('All')}</Option>
                    {multiple && Array.isArray(selected) && selected.length > 0 ?
                        <Option value="0" className="w-full uppercase font-bold text-[10px] text-right bg-transparent-hover"
                                style={{background: 'transparent'}}
                                onClick={() => onClear()}>{t('Clear')}</Option> : ''}

                </div> : (withAllButton ?
                    <Option value="0" className="w-full uppercase font-bold text-[10px] text-center"
                            onClick={() => onSelectAllClicked()}>{t('All')}</Option> : '')}
            {localList.map((item, index) => {
                const itemValue = getItemValue(item);
                const optionValue = itemValue.toString();
                const isSelected = multiple && Array.isArray(selected) && selected.some((entry) => areValuesEqual(entry, itemValue));
                return (<Option
                    value={optionValue}
                    className={`flex items-center ${isSelected ? 'bg-blue-gray-50 bg-opacity-80 text-blue-gray-900 dark:bg-blue-gray-900 dark:text-white' : ''}`}
                    onClick={() => onItemClicked(index)}
                    key={`${item.name.toString()}_${item.id?.toString()}_${item.code}`}>
                    {renderOption ? renderOption(item, !!isSelected) : <>{item.icon}{item.name}</>}
                </Option>);
            })}
            {showNoResultsAction ? (
                <div className="px-2 pb-2">
                    <button
                        type="button"
                        className="w-full rounded-md bg-blue-gray-50 px-3 py-2 text-sm font-semibold text-gray-700 transition hover:bg-blue-gray-100 dark:bg-blue-gray-900 dark:text-gray-200 dark:hover:bg-blue-gray-800"
                        onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            onCustomActionClick();
                        }}>
                        {noResultsActionLabel ? noResultsActionLabel(trimmedSearch) : ''}
                    </button>
                </div>
            ) : <></>}
        </Select>
    </div>

    // return <div className="mb-4 lg:mb-auto" id={id ? '#' + id : ''}>
    //     <Menu dismiss={{itemPress: false}} open={open && isShown}>
    //         <MenuHandler>
    //             <div
    //                 className={`relative ${multiple ? 'min-h-[2.5rem]' : 'h-10'} ${className ?? 'w-full min-w-[200px]'}`}>
    //                 <Button placeholder={''} onClick={() => setOpen(!open)}
    //                         className={`dropdown-border-important normal-case ${multiple ? 'min-h-[2.5rem]' : 'flex'} items-center text-left w-full peer h-full bg-transparent text-blue-gray-700 font-sans font-normal outline outline-0 focus:outline-0 disabled:bg-blue-gray-50 disabled:border-0 disabled:cursor-not-allowed transition-all placeholder-shown:border placeholder-shown:border-blue-gray-200 placeholder-shown:border-t-blue-gray-200 border focus:border-2 focus:border-t-transparent placeholder:opacity-0 focus:placeholder:opacity-100 text-sm px-3 py-2.5 rounded-[7px] border-blue-gray-200 focus:border-gray-900 dark:text-gray-500 dark:border-blue-gray-800 dark:placeholder-shown:border-blue-gray-800 dark:focus:border-blue-gray-800 dark:focus:border-t-transparent shadow-none hover:shadow-none ${buttonClassName ?? ''}`}>{label()}</Button>
    //                 <label
    //                     className={`flex w-full h-full select-none pointer-events-none absolute left-0 font-normal !overflow-visible truncate peer-placeholder-shown:text-blue-gray-500 leading-tight peer-focus:leading-tight peer-disabled:text-transparent peer-disabled:peer-placeholder-shown:text-blue-gray-500 transition-all -top-1.5 peer-placeholder-shown:text-sm text-[11px] peer-focus:text-[11px] before:content[' '] before:block before:box-border before:w-2.5 before:h-1.5 before:mt-[6.5px] before:mr-1 peer-placeholder-shown:before:border-transparent before:rounded-tl-md before:border-t peer-focus:before:border-t-2 before:border-l peer-focus:before:border-l-2 before:pointer-events-none before:transition-all peer-disabled:before:border-transparent after:content[' '] after:block after:flex-grow after:box-border after:w-2.5 after:h-1.5 after:mt-[6.5px] after:ml-1 peer-placeholder-shown:after:border-transparent after:rounded-tr-md after:border-t peer-focus:after:border-t-2 after:border-r peer-focus:after:border-r-2 after:pointer-events-none after:transition-all peer-disabled:after:border-transparent peer-placeholder-shown:leading-[3.75] text-gray-500 peer-focus:text-gray-900 before:border-blue-gray-200 peer-focus:before:!border-gray-900 after:border-blue-gray-200 peer-focus:after:!border-gray-900 dark:after:border-blue-gray-800 dark:before:border-blue-gray-800 dark:peer-focus:text-white dark:peer-focus:before:!border-blue-gray-800 dark:peer-focus:after:!border-blue-gray-800 ${labelClassName ?? ''}`}>{name}</label>
    //             </div>
    //         </MenuHandler>
    //         <MenuList
    //             ref={ref}
    //             className={`max-h-[250px] max-w-md mt-2 dark:bg-blue-gray-900 dark:text-gray-500 dark:border-blue-gray-800 ${menuClassName ?? 'w-full'}`}>
    //             {searchable && <input
    //                 className="w-full px-3 py-1 rounded border border-gray-700 dark:border-blue-gray-800 mb-2 bg-transparent outline-none placeholder:opacity-100 placeholder:text-gray-700"
    //                 placeholder={t('admin.datatable.search')} value={search} onChange={onSearchChanged}/>}
    //             {searchable && withLimit && list.length > 10 &&
    //                 <div
    //                     className="text-xs px-2 mb-2 text-gray-700 dark:text-gray-700 outline-none">{t('admin.datatable.only_10')}</div>}
    //             {withAllButton ? <MenuItem
    //                 className={`flex items-center`}
    //                 key={`${name.toString()}_all`}
    //                 onClick={() => onSelectAllClicked()}>{t('All')}</MenuItem> : ''}
    //             {localList.map((i, index) => {
    //                 return <MenuItem
    //                     className={`flex items-center ${multiple && Array.isArray(selected) && selected.includes(i.id ?? 0) ? 'bg-blue-gray-50 bg-opacity-80 text-blue-gray-900' : ''}`}
    //                     key={`${i.name.toString()}_${i.id?.toString()}_${i.code}`}
    //                     onClick={() => onItemClicked(index)}>{i.icon}{i.name}</MenuItem>
    //             })}
    //         </MenuList>
    //     </Menu>
    // </div>
}

export default memo(DropdownInput);
